import React from "react";
import { Card, CardContent, Typography, Box, Tooltip, LinearProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TimelineIcon from "@mui/icons-material/Timeline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export default function CohortCard({ windowDays, cohort }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const days = windowDays || cohort?.window_days || 30;
  const eligible = cohort?.eligible_branches ?? 0;
  const activated = cohort?.activated_branches ?? 0;
  const rawRate = cohort?.activation_rate;
  const isAvailable = cohort?.available !== false && eligible > 0 && rawRate !== null && rawRate !== undefined;
  const reason = cohort?.reason || (eligible === 0 ? `No branches registered within the ${days}-day window` : null);

  let formattedRate = "N/A";
  let progressVal = 0;

  if (isAvailable) {
    const numRate = typeof rawRate === "number" ? rawRate : parseFloat(rawRate);
    formattedRate = Number.isInteger(numRate) ? `${numRate}%` : `${numRate.toFixed(1)}%`;
    progressVal = Math.min(Math.max(numRate, 0), 100);
  }

  return (
    <Card
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: isDark
          ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)"
          : "#fff",
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)",
        color: isDark ? "#fff" : "#333",
        borderRadius: "14px",
        boxShadow: isDark ? "none" : "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
        transition: "all 0.25s ease",
      }}
    >
      <CardContent
        style={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Box
                style={{
                  backgroundColor: "rgba(99,102,241,0.12)",
                  borderRadius: "50%",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                  color: "#818cf8",
                }}
              >
                <TimelineIcon />
              </Box>
              <div>
                <Typography
                  variant="subtitle2"
                  style={{
                    fontWeight: 600,
                    color: isDark ? "rgba(255,255,255,0.9)" : "#111827",
                    fontSize: "14px",
                    lineHeight: 1.2,
                  }}
                >
                  Day-{days} Cohort
                </Typography>
                <Typography
                  variant="caption"
                  style={{
                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.5)",
                    fontSize: "11px",
                  }}
                >
                  Activation within {days} days
                </Typography>
              </div>
            </Box>
            {reason && (
              <Tooltip title={reason}>
                <HelpOutlineIcon
                  style={{
                    fontSize: 18,
                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                    cursor: "help",
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Rate Value */}
          <Box display="flex" alignItems="baseline" mb={1.5}>
            <Typography
              variant="h4"
              component="span"
              style={{
                fontWeight: 700,
                color: isAvailable ? (isDark ? "#fff" : "#111827") : (isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)"),
                fontSize: "30px",
                lineHeight: 1.1,
              }}
            >
              {formattedRate}
            </Typography>
            {isAvailable && (
              <Typography
                variant="body2"
                style={{
                  marginLeft: "6px",
                  color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                  fontSize: "13px",
                }}
              >
                activation rate
              </Typography>
            )}
          </Box>

          {/* Progress Bar */}
          <Box mb={2}>
            <LinearProgress
              variant="determinate"
              value={progressVal}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  background: "linear-gradient(90deg, #6366f1 0%, #3b82f6 100%)",
                },
              }}
            />
          </Box>
        </div>

        {/* Footer info / Counts */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          pt={1.5}
          borderTop={isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)"}
        >
          {isAvailable ? (
            <>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckCircleOutlineIcon style={{ fontSize: 15, color: "#34d399" }} />
                <Typography
                  variant="caption"
                  style={{
                    color: isDark ? "rgba(255,255,255,0.7)" : "#4b5563",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  {activated} of {eligible} branches
                </Typography>
              </Box>
              <Typography
                variant="caption"
                style={{
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.5)",
                  fontSize: "11px",
                }}
              >
                Activated
              </Typography>
            </>
          ) : (
            <Typography
              variant="caption"
              style={{
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.5)",
                fontStyle: "italic",
                fontSize: "11px",
              }}
            >
              {reason || "No cohort data available"}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
